/**
 * Service for fetching and caching configuration data from S3
 * Provides a cached interface to access JSON configuration data
 */

// Cache to store the fetched configuration data
let configCache: any = null

// Timestamp when the cache was last updated
let cacheTimestamp: number | null = null

// TTL for cache in milliseconds (10 minutes)
const CACHE_TTL = 10 * 60 * 1000

// Flag to track if a fetch is currently in progress
let isFetching = false

// Promise to track the ongoing fetch operation
let fetchPromise: Promise<any> | null = null

/**
 * Fetches JSON configuration data from the S3 path specified in REACT_APP_CONFIG_S3_PATH
 * @returns Promise that resolves to the parsed JSON configuration data
 */
const fetchConfigFromS3 = async (): Promise<any> => {
  // Get the S3 config path from environment variable
  const configS3Path = `${process.env.REACT_APP_CONFIG_S3_PATH}`

  // Throw error if the environment variable is not set
  if (!configS3Path) {
    throw new Error('REACT_APP_CONFIG_S3_PATH environment variable is not set')
  }

  // Fetch JSON data from S3 path
  const response = await fetch(configS3Path)

  // Check if the response is successful
  if (!response.ok) {
    throw new Error(`Failed to fetch config from S3: ${response.statusText}`)
  }

  // Parse and return the JSON response
  const configData = await response.json()
  return configData
}

/**
 * Gets the configuration data from cache or fetches it from S3 if not cached
 * Implements caching with TTL to avoid multiple fetches
 * @param forceRefresh - If true, forces a fresh fetch even if data is cached
 * @returns Promise that resolves to the configuration data
 */
export const getConfig = async (forceRefresh = false): Promise<any> => {
  // Check if cache is valid (exists and not expired)
  const isCacheValid =
    configCache && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_TTL

  // Return cached data if available, valid, and not forcing refresh
  if (isCacheValid && !forceRefresh) {
    return configCache
  }

  // If a fetch is already in progress, return the existing promise
  if (isFetching && fetchPromise) {
    return fetchPromise
  }

  // Set fetching flag and create new fetch promise
  isFetching = true
  fetchPromise = fetchConfigFromS3()
    .then((data) => {
      // Cache the fetched data and update timestamp
      configCache = data
      cacheTimestamp = Date.now()
      isFetching = false
      fetchPromise = null
      return data
    })
    .catch((error) => {
      // Reset flags on error
      isFetching = false
      fetchPromise = null
      throw error
    })

  return fetchPromise
}

/**
 * Gets a specific value from the configuration data
 * @param key - The key to retrieve from the config (supports nested keys with dot notation)
 * @param defaultValue - Optional default value if key is not found
 * @returns Promise that resolves to the config value or default value
 */
export const getConfigValue = async (
  key: string,
  defaultValue?: any
): Promise<any> => {
  try {
    // Get the full config data
    const config = await getConfig()

    // Support nested keys with dot notation (e.g., "logo" or "theme.colors.primary")
    const keys = key.split('.')
    let value = config

    // Traverse the nested object structure
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        // Return default value if key path doesn't exist
        return defaultValue
      }
    }

    return value !== undefined ? value : defaultValue
  } catch (error) {
    console.error(`Error getting config value for key "${key}":`, error)
    return defaultValue
  }
}

/**
 * Clears the cached configuration data
 * Useful for forcing a fresh fetch on the next getConfig call
 */
export const clearConfigCache = (): void => {
  configCache = null
  cacheTimestamp = null
  isFetching = false
  fetchPromise = null
}

/**
 * Gets the logo value from the configuration
 * @returns Promise that resolves to the logo URL or undefined
 */
export const getLogo = async (): Promise<string | undefined> => {
  return getConfigValue('logo')
}

/**
 * Gets the complete configuration object for a specific route
 * @param routeName - The route name/key to retrieve from the config (e.g., "mls", "tnt", "cwc")
 * @returns Promise that resolves to the route config object or undefined
 */
export const getRouteConfig = async (routeName: string): Promise<any> => {
  return getConfigValue(routeName)
}
